import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { FiCheck } from "react-icons/fi";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Loader from "../../components/Loader/Loader";
import { getConsultationService } from "../../api/consultationApi";
import { submitContactForm } from "../../api/contactApi";
import { ConsultantCard, ConsultantModal } from "./ConsultantCard";
import { CATEGORY_META, initials, resolveImage } from "./consultationUtils";
import "./Consultation.css";

const BOOKING_STEPS = [
  ["Select your consultation", "Choose the option that matches your requirement."],
  ["Choose your consultant", "Review profiles, experience, specialisation and charges."],
  ["Select date & time", "Choose an available consultation slot."],
  ["Share your details", "Enter your details and your primary consultation concern."],
  ["Make secure payment", "Review your booking and pay through our secure payment gateway."],
  ["Get your confirmation", "Your booking confirmation is provided after successful payment."],
];

// Order of sections when a service doesn't define its own `content.sections`.
const DEFAULT_SECTIONS = ["intro", "provide", "analysisSteps", "receive", "charges", "consultants", "howToBook", "closing"];

const Paragraphs = ({ items }) => (items || []).map((t, i) => <p key={i}>{t}</p>);

const SectionHead = ({ heading, subheading }) => (
  <>
    <h2 className="cn-h2">{heading}</h2>
    {subheading && <p className="cn-center cn-muted cn-subhead">{subheading}</p>}
  </>
);

const NumberedList = ({ items, className = "cn-steps" }) => (
  <ol className={className}>
    {items.map((s, i) => (
      <li key={s.title}>
        <span className="cn-funnel-num">{i + 1}</span>
        <div>
          <h4>{s.title}</h4>
          <p>{s.description}</p>
        </div>
      </li>
    ))}
  </ol>
);

/* ----------------------------------------------------- call to action ---- */

const emptyEnquiry = { name: "", phone: "", email: "", dob: "", currentNumber: "", note: "" };

// "Share your details" enquiry (goes to the Contact inbox) next to a "book and
// pay online" path that jumps to the consultants.
function CallToAction({ cta, service }) {
  const [form, setForm] = useState(emptyEnquiry);
  const [state, setState] = useState({ status: "idle", message: "" });

  const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\+?[0-9\s-]{10,16}$/.test(form.phone.trim())) {
      setState({ status: "error", message: "Please enter a valid phone number." });
      return;
    }
    setState({ status: "sending", message: "" });
    try {
      const lines = [
        `Enquiry for: ${service.title}`,
        form.dob && `Date of birth: ${form.dob}`,
        form.currentNumber && `Current mobile number: ${form.currentNumber}`,
        form.note && `Message: ${form.note}`,
      ].filter(Boolean);
      const result = await submitContactForm({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        message: lines.join("\n"),
      });
      if (!result.ok || !result.success) throw new Error(result.message || "Could not send your details.");
      setForm(emptyEnquiry);
      setState({ status: "done", message: "Thank you! Our team will contact you shortly." });
    } catch (err) {
      setState({ status: "error", message: err.message || "Could not send your details. Please try again." });
    }
  };

  return (
    <div className="cn-cta">
      <div className="cn-cta-copy">
        <h2>{cta.heading}</h2>
        {cta.text && <p>{cta.text}</p>}
        <a className="cn-btn cn-btn-gold" href="#cn-choose">
          {cta.bookLabel || "Book & pay online"}
        </a>
        <small>Secure payment via PayU · UPI, cards &amp; net banking</small>
      </div>

      <form className="cn-cta-form" onSubmit={submit} noValidate={false}>
        <h3>{cta.formHeading || "Share your details"}</h3>
        {cta.formText && <p className="cn-muted">{cta.formText}</p>}

        {state.status === "done" ? (
          <p className="cn-cta-done" role="status">{state.message}</p>
        ) : (
          <>
            <label className="cn-field">
              <span>Full name</span>
              <input required value={form.name} autoComplete="name" onChange={(e) => set("name", e.target.value)} />
            </label>
            <div className="cn-row">
              <label className="cn-field">
                <span>Phone / WhatsApp</span>
                <input required type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </label>
              <label className="cn-field">
                <span>Email</span>
                <input required type="email" inputMode="email" autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </label>
            </div>
            <div className="cn-row">
              <label className="cn-field">
                <span>Date of birth</span>
                <input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} />
              </label>
              <label className="cn-field">
                <span>Current mobile number</span>
                <input type="tel" inputMode="tel" value={form.currentNumber} onChange={(e) => set("currentNumber", e.target.value)} />
              </label>
            </div>
            <label className="cn-field">
              <span>What would you like to know?</span>
              <textarea rows="3" maxLength={500} value={form.note} onChange={(e) => set("note", e.target.value)} />
            </label>
            {state.status === "error" && <p className="cn-error" role="alert">{state.message}</p>}
            <button type="submit" className="cn-btn cn-btn-navy" disabled={state.status === "sending"}>
              {state.status === "sending" ? "Sending..." : "Send my details"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}

/* --------------------------------------------------------- testimonials -- */

function Testimonials({ block }) {
  const [open, setOpen] = useState(null);
  const items = block.items || [];

  return (
    <>
      <SectionHead heading={block.heading || "What our customers say"} subheading={block.subheading} />
      <div className="cn-testimonials">
        {items.map((t) => (
          <article className="cn-testimonial" key={t.name + (t.summary || "")}>
            {t.photo ? (
              <img className="cn-testimonial-photo" src={resolveImage(t.photo)} alt={t.name} loading="lazy" />
            ) : (
              <span className="cn-testimonial-photo cn-photo-fallback" aria-hidden="true">{initials(t.name)}</span>
            )}
            <h3>{t.name}</h3>
            {t.location && <p className="cn-muted">{t.location}</p>}
            {t.summary && <p className="cn-testimonial-summary">“{t.summary}”</p>}
            {t.story && (
              <button type="button" className="cn-btn cn-btn-ghost" onClick={() => setOpen(t)}>
                Read about {t.name.split(" ")[0]}
              </button>
            )}
          </article>
        ))}
      </div>

      {open && (
        <div className="cn-modal-overlay" onClick={() => setOpen(null)}>
          <div className="cn-modal" role="dialog" aria-modal="true" aria-label={open.name} onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cn-modal-close" onClick={() => setOpen(null)} aria-label="Close">×</button>
            <div className="cn-consultant-head">
              {open.photo ? (
                <img className="cn-photo cn-photo-lg" src={resolveImage(open.photo)} alt={open.name} />
              ) : (
                <span className="cn-photo cn-photo-lg cn-photo-fallback" aria-hidden="true">{initials(open.name)}</span>
              )}
              <div>
                <h3>{open.name}</h3>
                {open.location && <p className="cn-muted">{open.location}</p>}
              </div>
            </div>
            <div className="cn-bio">{open.story}</div>
            <p className="cn-muted cn-small">Results vary from person to person and are not guaranteed.</p>
          </div>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ page -- */

export default function ConsultationService() {
  const { category, slug } = useParams();
  const navigate = useNavigate();

  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const result = await getConsultationService(slug);
        if (cancelled) return;
        if (!result.ok || !result.success) throw new Error(result.status === 404 ? "Service not found" : result.message || "Unable to load service");
        setService(result.data);
      } catch (e) {
        if (!cancelled) setError(e.message || "Unable to load service");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const book = (consultant) => navigate(`/consultation/book/${slug}${consultant ? `?consultant=${consultant.id}` : ""}`);

  if (loading) {
    return (
      <>
        <Header />
        <div className="cn-state"><Loader label="Loading..." /></div>
        <Footer />
      </>
    );
  }

  if (error || !service) {
    return (
      <>
        <Header />
        <div className="cn-state">
          <h2>{error || "Service not found"}</h2>
          <Link className="cn-btn cn-btn-gold" to="/consultation">Browse consultations</Link>
        </div>
        <Footer />
      </>
    );
  }

  const c = service.content || {};
  const meta = CATEGORY_META[service.category] || CATEGORY_META[category] || {};
  const hasConsultants = service.consultants.length > 0;

  // One renderer per section; `content.sections` (or the default) picks which
  // appear and in what order. A section with no content renders nothing.
  const renderers = {
    intro: () =>
      c.intro?.length > 0 && (
        <section className="cn-section">
          <div className="cn-container cn-prose">
            <Paragraphs items={c.intro} />
          </div>
        </section>
      ),

    about: () =>
      c.about && (
        <section className="cn-section">
          <div className="cn-container cn-prose">
            <SectionHead heading={c.about.heading} subheading={c.about.subheading} />
            <Paragraphs items={c.about.paragraphs} />
          </div>
        </section>
      ),

    provide: () =>
      c.provide?.length > 0 && (
        <section className="cn-section cn-section-alt">
          <div className="cn-container">
            <h2 className="cn-h2">What will we provide you?</h2>
            <div className="cn-provide">
              {c.provide.map((block, i) => (
                <article className="cn-provide-card" key={block.title}>
                  <span className="cn-provide-num">{String(i + 1).padStart(2, "0")}</span>
                  <h3>{block.title}</h3>
                  <p>{block.description}</p>
                  {block.points?.length > 0 && (
                    <ul>
                      {block.points.map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ul>
                  )}
                  {block.note && <p className="cn-note">Note: {block.note}</p>}
                </article>
              ))}
            </div>
            {c.provideNote && <p className="cn-callout">{c.provideNote}</p>}
          </div>
        </section>
      ),

    analysisSteps: () =>
      c.analysisSteps?.length > 0 && (
        <section className="cn-section">
          <div className="cn-container">
            <h2 className="cn-h2">How will we analyse your chart?</h2>
            <NumberedList items={c.analysisSteps} />
            {c.concernOptions?.length > 0 && (
              <div className="cn-chips cn-chips-center">
                {c.concernOptions.map((o) => (
                  <span className="cn-chip" key={o}>{o}</span>
                ))}
              </div>
            )}
          </div>
        </section>
      ),

    process: () =>
      c.process?.steps?.length > 0 && (
        <section className="cn-section cn-section-alt">
          <div className="cn-container">
            <SectionHead heading={c.process.heading} subheading={c.process.subheading} />
            <NumberedList items={c.process.steps} className="cn-steps cn-steps-col" />
          </div>
        </section>
      ),

    howToStart: () =>
      c.howToStart?.steps?.length > 0 && (
        <section className="cn-section">
          <div className="cn-container">
            <SectionHead heading={c.howToStart.heading} subheading={c.howToStart.subheading} />
            <NumberedList items={c.howToStart.steps} className="cn-funnel" />
          </div>
        </section>
      ),

    whyChoose: () =>
      c.whyChoose?.points?.length > 0 && (
        <section className="cn-section cn-section-alt">
          <div className="cn-container">
            <SectionHead heading={c.whyChoose.heading} subheading={c.whyChoose.subheading} />
            <div className="cn-why">
              {c.whyChoose.points.map((p) => (
                <article className="cn-why-card" key={p.title}>
                  <FiCheck aria-hidden="true" />
                  <h3>{p.title}</h3>
                  <p>{p.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      ),

    cta: () =>
      c.cta && (
        <section className="cn-section cn-cta-section">
          <div className="cn-container">
            <CallToAction cta={c.cta} service={service} />
          </div>
        </section>
      ),

    testimonials: () =>
      c.testimonials?.items?.length > 0 && (
        <section className="cn-section">
          <div className="cn-container">
            <Testimonials block={c.testimonials} />
          </div>
        </section>
      ),

    receive: () =>
      c.receive?.length > 0 && (
        <section className="cn-section cn-section-alt">
          <div className="cn-container">
            <SectionHead
              heading={c.receiveHeading || "What will you receive?"}
              subheading={c.receiveSubheading || "Depending on the consultation package you select:"}
            />
            <ul className="cn-receive">
              {c.receive.map((r) => (
                <li key={r}>
                  <FiCheck /> {r}
                </li>
              ))}
            </ul>
          </div>
        </section>
      ),

    charges: () => (
      <section className="cn-section">
        <div className="cn-container cn-prose">
          <SectionHead heading={c.charges?.heading || "Consultation charges"} subheading={c.charges?.subheading} />
          {c.charges?.paragraphs ? (
            <Paragraphs items={c.charges.paragraphs} />
          ) : (
            <p>
              The consultation fee may vary depending on the consultation type, duration, consultant, scope of
              analysis and report requirement. Choose the consultation that best matches your requirement.
            </p>
          )}
        </div>
      </section>
    ),

    consultants: () => (
      <section className="cn-section cn-section-alt" id="cn-choose">
        <div className="cn-container">
          <SectionHead
            heading={c.consultantsSection?.heading || "Choose your consultant"}
            subheading={c.consultantsSection?.subheading || "Select the consultant you would like to consult with."}
          />
          {hasConsultants ? (
            <div className="cn-consultants">
              {service.consultants.map((consultant) => (
                <ConsultantCard key={consultant.id} consultant={consultant} onView={setProfile} onSelect={book} />
              ))}
            </div>
          ) : (
            <p className="cn-state">Consultants for this service will be available soon.</p>
          )}
        </div>
      </section>
    ),

    howToBook: () => (
      <section className="cn-section">
        <div className="cn-container">
          <h2 className="cn-h2">How to book your consultation</h2>
          <ol className="cn-funnel">
            {BOOKING_STEPS.map(([title, text], i) => (
              <li key={title}>
                <span className="cn-funnel-num">{i + 1}</span>
                <div>
                  <h4>{title}</h4>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
    ),

    closing: () => (
      <section className="cn-closing">
        <div className="cn-container">
          <h2>{c.closingHeading || "Your consultation, your questions."}</h2>
          {c.closingText && <p>{c.closingText}</p>}
          {hasConsultants && (
            <a className="cn-btn cn-btn-gold" href="#cn-choose">
              Book {service.title}
            </a>
          )}
        </div>
      </section>
    ),
  };

  const order = Array.isArray(c.sections) && c.sections.length > 0 ? c.sections : DEFAULT_SECTIONS;

  return (
    <>
      <Header />

      <section className="cn-hero cn-hero-left">
        <div className={`cn-hero-inner ${service.imageUrl ? "cn-hero-split" : ""}`}>
          <div>
            <nav className="cn-crumbs" aria-label="Breadcrumb">
              <Link to="/consultation">Consultation</Link> / <Link to={`/consultation/${service.category}`}>{meta.label}</Link>
            </nav>
            <h1>{service.title}</h1>
            {service.tagline && <p className="cn-lead">{service.tagline}</p>}
            {!service.tagline && service.shortDescription && <p className="cn-lead">{service.shortDescription}</p>}
            <a className="cn-btn cn-btn-gold" href="#cn-choose">
              Choose your consultant &amp; book now
            </a>
          </div>
          {service.imageUrl && <img className="cn-hero-img" src={resolveImage(service.imageUrl)} alt={service.title} />}
        </div>
      </section>

      {order.map((key) => {
        const render = renderers[key];
        const node = render ? render() : null;
        return node ? <React.Fragment key={key}>{node}</React.Fragment> : null;
      })}

      {profile && (
        <ConsultantModal
          consultant={profile}
          onClose={() => setProfile(null)}
          onSelect={(consultant) => {
            setProfile(null);
            book(consultant);
          }}
        />
      )}

      <Footer />
    </>
  );
}
